import { useCallback, useState } from "react";

import type { CreateQueueRequest, ProvisionModeUI } from "../../../types";

type QueueType = "rabbitmq" | "nats" | "kafka" | "pulsar";

export interface QueueFormState {
  name: string;
  queueType: QueueType;
  mode: ProvisionModeUI;
  // RabbitMQ
  replicas: 1 | 3;
  persistence: boolean;
  managementUi: boolean;
  // NATS
  jetStream: boolean;
  // Kafka
  partitions: number;
  replicationFactor: 1 | 3;
  // Advanced
  tls: boolean;
  auth: "none" | "token" | "mtls";
}

export const INITIAL_QUEUE_STATE: QueueFormState = {
  name: "",
  queueType: "rabbitmq",
  mode: "shared",
  replicas: 3,
  persistence: true,
  managementUi: true,
  jetStream: true,
  partitions: 3,
  replicationFactor: 3,
  tls: false,
  auth: "token",
};

export interface QueueFormErrors {
  name?: string;
}

const NAME_RE = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

export function useQueueForm() {
  const [state, setState] = useState<QueueFormState>(INITIAL_QUEUE_STATE);
  const [errors, setErrors] = useState<QueueFormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const validateField = useCallback((field: string, value: string) => {
    if (field === "name") {
      if (!value.trim()) return "Queue name is required";
      if (!NAME_RE.test(value))
        return "Must start with a letter or underscore, only alphanumeric and underscore";
      if (value.length > 50) return "Name must be 50 characters or less";
    }
    return undefined;
  }, []);

  const setField = useCallback(
    <K extends keyof QueueFormState>(field: K, value: QueueFormState[K]) => {
      setState((s) => ({ ...s, [field]: value }));
      if (touched[field as string]) {
        const err = validateField(field as string, String(value));
        setErrors((e) => ({ ...e, [field as string]: err }));
      }
    },
    [touched, validateField],
  );

  const onBlur = useCallback(
    (field: string) => {
      setTouched((t) => ({ ...t, [field]: true }));
      const err = validateField(
        field,
        String(state[field as keyof QueueFormState] ?? ""),
      );
      setErrors((e) => ({ ...e, [field]: err }));
    },
    [state, validateField],
  );

  const validate = useCallback((): boolean => {
    const newErrors: QueueFormErrors = {};
    const nameErr = validateField("name", state.name);
    if (nameErr) newErrors.name = nameErr;
    setErrors(newErrors);
    setTouched({ name: true });
    return Object.keys(newErrors).length === 0;
  }, [state, validateField]);

  const buildRequest = useCallback((): CreateQueueRequest => {
    const req: CreateQueueRequest = {
      name: state.name,
      queueType: state.queueType,
      mode: state.mode,
    };
    if (state.mode === "dedicated") {
      if (state.queueType === "rabbitmq") {
        req.replicas = state.replicas;
        req.persistence = state.persistence;
        req.managementUi = state.managementUi;
      }
      if (state.queueType === "nats") {
        req.jetStream = state.jetStream;
      }
      if (state.queueType === "kafka") {
        req.partitions = state.partitions;
        req.replicationFactor = state.replicationFactor;
      }
    }
    if (state.tls) req.tls = state.tls;
    if (state.auth !== "token") req.auth = state.auth;
    return req;
  }, [state]);

  return { state, errors, setField, onBlur, validate, buildRequest };
}
